import React, { useEffect, useState } from 'react';
import { Empty, List, Pagination } from 'antd';
import styled from 'styled-components';
import * as QueryString from 'query-string';
// import { UsergroupAddOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router';
import UserListItem from './UserListItem';
import { Message } from '../../shared/Message';
import { useListUsersQuery } from '../../../graphql/user.generated';
import { CorpUser } from '../../../types.generated';
import TabToolbar from '../../entity/shared/components/styled/TabToolbar';
import { SearchBar } from '../../search/SearchBar';
import { useEntityRegistry } from '../../useEntityRegistry';
import ViewInviteTokenModal from './ViewInviteTokenModal';
import { useListRolesQuery } from '../../../graphql/role.generated';
import { scrollToTop } from '../../shared/searchUtils';
import { DEFAULT_USER_LIST_PAGE_SIZE } from './cacheUtils';
import { useUserContext } from '../../context/useUserContext';
import { ComponentSpinner } from '../../shared/ComponentSpinner';
import { useTranslation } from '../../../i18n/useTranslation';

const UserContainer = styled.div``;

const UserPaginationContainer = styled.div`
    display: flex;
    justify-content: center;
`;

export const UserList = () => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const location = useLocation();
    const params = QueryString.parse(location.search, { arrayFormat: 'comma' });
    const paramsQuery = (params?.query as string) || undefined;
    const [query, setQuery] = useState<undefined | string>(undefined);
    useEffect(() => setQuery(paramsQuery), [paramsQuery]);

    const [page, setPage] = useState(1);
    const [isViewingInviteToken, setIsViewingInviteToken] = useState(false);

    const authenticatedUser = useUserContext();
    const canManagePolicies = authenticatedUser?.platformPrivileges?.managePolicies || false;

    const pageSize = DEFAULT_USER_LIST_PAGE_SIZE;
    const start = (page - 1) * pageSize;

    const {
        loading: usersLoading,
        error: usersError,
        data: usersData,
    } = useListUsersQuery({
        variables: {
            input: {
                start,
                count: pageSize,
                query: (query?.length && query) || undefined,
            },
        },
        fetchPolicy: (query?.length || 0) > 0 ? 'no-cache' : 'cache-first',
    });

    const totalUsers = usersData?.listUsers?.total || 0;
    const users = usersData?.listUsers?.users || [];

    const onChangePage = (newPage: number) => {
        scrollToTop();
        setPage(newPage);
    };

    const { loading: rolesLoading, error: rolesError } = useListRolesQuery({
        fetchPolicy: 'cache-first',
        variables: {
            input: {
                start: 0,
                count: 10,
            },
        },
    });

    const loading = usersLoading || rolesLoading;
    const error = usersError || rolesError;

    return (
        <>
            {error && <Message type="error" content="Failed to load users! An unexpected error occurred." />}
            <UserContainer tabIndex={-1}>
                <TabToolbar>
                    <div>
                        {/* TODO: Hiding Invite Users implementation for now */}
                        {/* <Button disabled={!canManagePolicies} type="text" onClick={() => setIsViewingInviteToken(true)}>
                            <UsergroupAddOutlined /> Invite Users
                        </Button> */}
                    </div>
                    <SearchBar
                        initialQuery={query || ''}
                        placeholderText={t('Search Users')}
                        suggestions={[]}
                        style={{
                            maxWidth: 270,
                            padding: 0,
                        }}
                        inputStyle={{
                            height: 32,
                            fontSize: 14,
                        }}
                        onSearch={() => null}
                        onQueryChange={(q) => {
                            setQuery(q);
                            setPage(1);
                        }}
                        entityRegistry={entityRegistry}
                        hideRecommendations
                    />
                </TabToolbar>
                {!usersData && loading && <ComponentSpinner />}
                <List
                    bordered
                    locale={{
                        emptyText: <Empty description={t('No Users!')} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    }}
                    dataSource={users as CorpUser[]}
                    renderItem={(item: CorpUser) => <UserListItem user={item} />}
                />
                <UserPaginationContainer>
                    <Pagination
                        style={{ margin: 40 }}
                        current={page}
                        pageSize={pageSize}
                        total={totalUsers}
                        showLessItems
                        onChange={onChangePage}
                        showSizeChanger={false}
                    />
                </UserPaginationContainer>
                {canManagePolicies && (
                    <ViewInviteTokenModal
                        visible={isViewingInviteToken}
                        onClose={() => setIsViewingInviteToken(false)}
                    />
                )}
            </UserContainer>
        </>
    );
};
